<template>
  <a-layout class="full-window" has-sider>
    <meta-info :title="props.title"> </meta-info>
    <a-layout-sider
      class="aside-container"
      :collapsed="menu_store.is_collapsed"
      :width="side_menu_width"
    >
      <sidebar v-if="menu_store.is_visible"> </sidebar>
      <a-drawer
        v-else
        v-model="menuDrawerVisibility"
        direction="ltr"
        :with-header="false"
        :size="side_menu_width"
        class="menu-drawer"
      >
        <sidebar></sidebar>
      </a-drawer>
    </a-layout-sider>
    <a-layout class="main-wrapper">
      <a-layout-header class="header">
        <a-col :span="24">
          <a-row>
            <a-col flex="auto">
              <a-breadcrumb class="breadcrumb">
                <a-breadcrumb-item v-for="(item, index) in layout_store.breadcrumb" :key="item">
                  <router-link v-if="item.path" :to="item.path"> {{ item.label }} </router-link>
                  <span v-else>{{ item.label }}</span>
                </a-breadcrumb-item>
              </a-breadcrumb>
            </a-col>
            <a-col flex="auto">
              <main-header></main-header>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="24">
              <slot name="header-content"></slot>
            </a-col>
          </a-row>
        </a-col>
      </a-layout-header>
      <h-divider class="header-divider"></h-divider>
      <a-layout-content id="main-container" ref="main_container" @scroll.passive="handleScroll">
        <div class="main-content">
          <slot></slot>
        </div>
        <a-layout-footer :height="footer_height" v-if="settings_store.isFooterVisible">
          <slot name="footer"></slot>
          <a-row class="foot-notes-row">
            <foot-notes></foot-notes>
          </a-row>
        </a-layout-footer>
      </a-layout-content>
      <!-- <el-backtop
        :right="100"
        :bottom="50"
        target="#main-container"
        class="back-top"
      >
        <div class="back-top-content">
          <div class="back-top-icon"></div>
        </div>
      </el-backtop> -->
    </a-layout>
  </a-layout>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useMenuStore } from '@/stores/menu'
import HDivider from '@/components/core/h-divider.vue'
import FootNotes from '@/components/core/foot-notes.vue'
import sidebar from '@/components/core/sidebar.vue'
import metaInfo from '@/components/meta/meta-info.vue'
import mainHeader from '@/components/core/main-header.vue'
import { useScrollStore } from '@/stores/scroll'
import { useSettingsStore } from '@/stores/settings'
import { useLayoutStore } from '@/stores/layout'

const props = defineProps<{ title?: string }>()
const full_height = ref(0)
const header_height = '74px'
const footer_height = '64px'
const offset_height = '97px'
const main_container = ref<{ $el: HTMLElement }>()
const settings_store = useSettingsStore()
const menu_store = useMenuStore()
const scroll_store = useScrollStore()
const layout_store = useLayoutStore()
const side_menu_width = computed(() =>
  menu_store.is_collapsed ? (menu_store.is_visible ? 80 : 0) : 250,
)
const side_menu_width_px = computed(() => `${side_menu_width.value}px`)
const main_padding = computed(() => (menu_store.is_visible ? '20px' : '0'))

const menuDrawerVisibility = computed({
  get: () => !menu_store.is_collapsed,
  set: (value) => {
    setTimeout(() => menu_store.setCollapsed(!value), 0)
  },
})

function resize() {
  full_height.value = main_container.value?.$el.offsetHeight || 0
  scroll_store.setScrollBoxHeight(main_container.value?.$el.offsetHeight || 0)
}

onUnmounted(() => {
  window.removeEventListener('resize', resize)
})
onMounted(() => {
  resize()
  scroll_store.setScrollBoxHeight(main_container.value?.$el.offsetHeight || 0)
  if (!menu_store.is_visible) {
    menu_store.setCollapsed(true)
  }
  window.addEventListener('resize', resize)
})

function handleScroll(event: Event) {
  const el = event.target as HTMLElement
  scroll_store.setScrollTop(el?.scrollTop)
}
const scrollTo = ref(0)
scroll_store.$subscribe(async () => {
  if (scroll_store.scrollTop !== scrollTo.value) {
    scrollTo.value = scroll_store.scrollTop
    main_container.value?.$el.scrollTo({ top: scrollTo.value })
  }
})
</script>
<style>
.a-layout-content {
  --a-layout-content-padding: v-bind(main_padding);
  padding-bottom: 0;
}
.aside-container {
  overflow: 'auto';
  height: '100vh';
  position: 'fixed';
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 3;
  color: var(--sidebar-text);
  width: v-bind(side_menu_width_px);
  background: var(--sidebar-background);
  transition: width 300ms;
  -webkit-box-shadow: var(--sidebar-shadow);
  box-shadow: var(--sidebar-shadow);
}
.header-divider {
  z-index: 1;
  top: -1px;
  position: relative;
}
#main-container {
  overflow: scroll;
}
.main-content {
  display: flex;
  justify-content: center;
  overflow: scroll;
  width: 100%;
  max-height: calc(v-bind(full_height) * 1px);
  height: calc(calc(v-bind(full_height) * 1px) - var(--footer-height));
  min-height: calc(100% - v-bind(offset_height));
}

.secondary-title {
  padding: 2px 0 0 5px;
}

.main-wrapper {
  transition: width 0.3s;
}

.header {
  position: relative;
  align-items: baseline;
  width: 100%;
  height: v-bind(header_height) !important;
  padding: 0;
  background-color: var(--color-header-background);
}

.header-row {
  width: 100%;
}

.breadcrumb {
  padding: 20px 10px;
}

.foot-notes-row {
  align-items: baseline;
  vertical-align: bottom;
}

/* Transition */
.slide-fade-enter-active {
  transition: all 0.3s;
  animation-timing-function: linear;
}

.slide-fade-leave-active {
  transition: all 0.3s;
  animation-timing-function: linear;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  animation-timing-function: linear;
  transform: translateX(20px);
  opacity: 0;
  width: 0;
}

.back-top {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  font-variant: tabular-nums;
  line-height: 1.5;
  list-style: none;
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
  position: fixed;
  right: 100px;
  bottom: 50px;
  z-index: 10;
  width: 40px;
  height: 40px;
  cursor: pointer;
}
.back-top-content {
  width: 40px;
  height: 40px;
  overflow: hidden;
  color: #fff;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.45);
  border-radius: 20px;
  -webkit-transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.back-top-icon {
  width: 14px;
  height: 16px;
  margin: 12px auto;
  background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAoCAYAAACWwljjAAAABGdBTUEAALGPC/xhBQAAAbtJREFUWAntmMtKw0AUhhMvS5cuxILgQlRUpIggIoKIIoigG1eC+AA+jo+i6FIXBfeuXIgoeKVeitVWJX5HWhhDksnUpp3FDPyZk3Nm5nycmZKkXhAEOXSA3lG7muTeRzmfy6HneUvIhnYkQK+Q9NhAA0Opg0vBEhjBKHiyb8iGMyQMOYuK41BcBSypAL+MYXSKjtFAW7EAGEO3qN4uMQbbAkXiSfRQJ1H6a+yhlkKRcAoVFYiweYNjtCVQJJpBz2GCiPt7fBOZQpFgDpUikse5HgnkM4Fi4QX0Fpc5wf9EbLqpUCy4jMoJSXWhFwbMNgWKhVbRhy5jirhs9fy/oFhgHVVTJEs7RLZ8sSEoJm6iz7SZDMbJ+/OKERQTttCXQRLToRUmrKWCYuA2+jbN0MB4OQobYShfdTCgn/sL1K36M7TLrN3n+758aPy2rrpR6+/od5E8tf/A1uLS9aId5T7J3CNYihkQ4D9PiMdMC7mp4rjB9kjFjZp8BlnVHJBuO1yFXIV0FdDF3RlyFdJVQBdv5AxVdIsq8apiZ2PyYO1EVykesGfZEESsCkweyR8MUW+V8uJ1gkYipmpdP1pm2aJVPEGzAAAAAElFTkSuQmCC)
    100%/100% no-repeat;
}
</style>
